import { ResourcePermissionsMap, UserRole, useUserRole } from "common";
import { createContext, ReactNode, useContext } from "react";

const homeRoutesByRole: Record<UserRole, string> = {
  [UserRole.ADMIN]: "/alerts",
  [UserRole.INSTALLER]: "/alerts",
  [UserRole.USER]: "/alerts",
  [UserRole.UNAUTHENTICATED]: "/login",
};

interface UserRoleContextType {
  role: UserRole | null;
  permissions: ResourcePermissionsMap | null;
  homeRoute: string;
  isInitialized: boolean;
  isRefreshingUserRole: boolean;
  refreshUserRole?: () => Promise<UserRole | undefined>;
}

export const UserRoleContext = createContext<UserRoleContextType>({
  role: null,
  permissions: null,
  homeRoute: homeRoutesByRole[UserRole.UNAUTHENTICATED],
  isInitialized: false,
  isRefreshingUserRole: false,
});

interface UserRoleProviderProps {
  children: ReactNode;
}

export const UserRoleProvider = ({ children }: UserRoleProviderProps) => {
  const {
    role,
    permissions,
    isInitialized,
    isFetchingUserRole,
    fetchUserRole,
  } = useUserRole();

  const homeRoute =
    role && role in homeRoutesByRole
      ? homeRoutesByRole[role]
      : homeRoutesByRole[UserRole.UNAUTHENTICATED];

  return (
    <UserRoleContext.Provider
      value={{
        role,
        permissions,
        homeRoute,
        isInitialized,
        isRefreshingUserRole: isFetchingUserRole,
        refreshUserRole: fetchUserRole,
      }}
    >
      {children}
    </UserRoleContext.Provider>
  );
};

export const useUserRoleContext = () => {
  const context = useContext(UserRoleContext);

  if (!context) {
    throw new Error(
      "useUserRoleContext must be used within a UserRoleProvider",
    );
  }

  return context;
};
